import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { Pagination } from '@/dsm-components';
import { useGetOrganizationsWithHardwareExceptionsQuery } from '@/services/organizations';
import { type DataTableColumn, PalmettoDataTable, PalmettoTag, PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { OrgHardwareExceptionSummary } from 'types';

const pageSize = 25;

const exceptionTag = (granted: boolean) =>
  granted ? <PalmettoTag label="Yes" color="success" /> : <PalmettoTag label="No" color="grey" />;

export const HardwareExceptionsTable = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching, error, refetch } = useGetOrganizationsWithHardwareExceptionsQuery({
    ...(search ? { search } : {}),
    limit: pageSize,
    offset: page * pageSize,
  });

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(0);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => setPage(nextPage), []);

  const getRowId = useCallback((row: OrgHardwareExceptionSummary) => row.alias, []);

  const columns: DataTableColumn<OrgHardwareExceptionSummary>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Organization',
        cellType: 'link',
        render: (row) => <Link to={`/settings/organizations/${row.alias}`}>{row.name}</Link>,
      },
      {
        id: 'hasCategoryException',
        header: 'Category-Level Exception',
        width: '200px',
        cellType: 'tag',
        render: (row) => exceptionTag(row.hasCategoryException),
      },
      {
        id: 'hasSpecificException',
        header: 'Specific Hardware Exception',
        width: '220px',
        cellType: 'tag',
        render: (row) => exceptionTag(row.hasSpecificException),
      },
      {
        id: 'hasChildOrganizations',
        header: 'Child Organizations',
        width: '180px',
        cellType: 'tag',
        render: (row) => exceptionTag(row.hasChildOrganizations),
      },
    ],
    [],
  );

  return (
    <DsmPageContainer title="Hardware Exceptions">
      <PageHeader title="Hardware Exceptions" />

      <div className="mx-6 mb-4">
        <PalmettoTextInput
          id="hardware-exceptions-search"
          label="Search"
          placeholder="Search organization name"
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      <div className="mx-6 overflow-x-auto">
        <PalmettoDataTable
          columns={columns}
          data={items}
          getRowId={getRowId}
          isLoading={isLoading || isFetching}
          error={error}
          onRetry={refetch}
          ariaLabel="Hardware exceptions"
        />
      </div>

      {!error && totalPages > 1 && (
        <div className="mx-6 mt-4">
          <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      )}
    </DsmPageContainer>
  );
};
